import {Component, OnInit} from '@angular/core';
import {Store} from "@ngrx/store";
import * as fromStore from "../../../../store";
import {ActivatedRoute} from "@angular/router";
import {filter, first, switchMap, takeUntil} from "rxjs/operators";
import {FormArray, FormBuilder} from "@angular/forms";
import {TextUtils} from "../../../../shared/utils/text-utils";
import {WooCommerceSetting, WooCommerceSettings} from "./ecommerce-settings.interface";
import {EcommerceSettingsService} from "./ecommerce-settings.service";
import {NotifierComponent} from "../../../../shared/components/notifier/notifier.component";
import {Notification} from "../../../../shared/components/notifier/notification";
import {NotificationType} from "../../../../shared/components/notifier/notification-type";
import {Config} from "../../../../config/config";
import {combineLatest, Subject} from "rxjs";

@Component({
    selector: 'db-ecommerce-settings',
    templateUrl: './ecommerce-settings.component.html',
    styleUrls: ['./ecommerce-settings.component.scss'],
    standalone: false
})
export class EcommerceSettingsComponent implements OnInit {
  private _website = this._activatedRoute.snapshot.parent.parent.data['website'];
  settingsForm = this._fb.group({
    settings: this._fb.array([
      this._fb.group({
        id: 'woocommerce_weight_unit',
        value: null,
      }),
      this._fb.group({
        id: 'woocommerce_dimension_unit',
        value: null,
      }),
      this._fb.group({
        id: 'woocommerce_default_country',
        value: null,
      }),
      this._fb.group({
        id: 'woocommerce_currency',
        value: null,
      }),
    ])
  });
  dropdownOptions = {};
  countryCurrencyCodes = {};
  private _defaultValue;
  private _destroyStream$ = new Subject<boolean>();
  loading = true;
  connectTaxesEnabled: boolean;
  isFakeMode = this._website.id === Config.demoDomainId;

  get settingsFormArray(): FormArray {
    return this.settingsForm.get('settings') as FormArray;
  }

  constructor(private _activatedRoute: ActivatedRoute,
              private _fb: FormBuilder,
              private _settingsService: EcommerceSettingsService,
              private _store: Store<fromStore.DbStoreState>) {
  }

  ngOnInit() {
    this._store.dispatch(new fromStore.LoadEcommerceSettings({websiteId: this._website.id}));
    this._getSettings();
    this._getGeneralSettings();
  }

  private _getGeneralSettings(): void {
    this._store.select(fromStore.getEcommerceGeneralSettingsLoaded(this._website?.id))
      .pipe(
        filter((loaded: boolean) => loaded),
        switchMap(() => combineLatest(
          this._store.select(fromStore.getEcommerceConnectTaxesEnabled(this._website?.id))
        )),
        filter(([connectTaxesEnabled]) => !!connectTaxesEnabled ),
        takeUntil(this._destroyStream$)
      )
      .subscribe(([connectTaxesEnabled]) => {
        this.connectTaxesEnabled = connectTaxesEnabled?.value === 'yes';
      });
  }

  private _getSettings() {
    this._store.select(fromStore.getSettings(this._website.id))
      .pipe(
        first(value => !!value)
      ).subscribe((response) => {
      this.loading = false;
      this.countryCurrencyCodes = response.country_currency_codes;
      this._setFormValues(response);
      this._defaultValue = this.settingsForm.getRawValue();
    });
  }

  private _setFormValues(data: WooCommerceSettings) {
    this.settingsFormArray.controls.map(control => {
      const controlName = control.get('id').value;
      const setting: WooCommerceSetting = data.settings.find(sett => sett.id === controlName);
      this.dropdownOptions[controlName] = Object.keys(setting.options).map(key => ({
        value: key,
        label: this._decodeHtmlEntities(setting.options[key])
      }));
      control.get('value').setValue(setting.value);
    });
  }

  private _decodeHtmlEntities = (htmlString: string): string => {
    const doc = new DOMParser().parseFromString(htmlString, 'text/html');
    return doc.body.textContent || "";
  }


  regionSelected(value) {
    this.settingsFormArray.at(3).get('value').setValue(this.countryCurrencyCodes[value]);
  }

  discardForm() {
    this.settingsForm.patchValue(this._defaultValue);
  }

  saveSettings() {
    this.loading = true;
    const value = this.settingsForm.getRawValue();
    this._settingsService.updateSettings(this._website.id, {update: value.settings})
      .pipe(filter(resp => !!resp))
      .subscribe((response) => {
        this.loading = false;
        if (response.success) {
          NotifierComponent.notify(new Notification(TextUtils.getText('text_settingsUpdated'), NotificationType.SUCCESS));
        } else {
          NotifierComponent.notify(new Notification(TextUtils.getText('text_somethingWentWrong'), NotificationType.DANGER));
        }
      });
  }

}
